/**
 * Site Manager Cockpit — Microsoft sign-in via MSAL.js (browser), npm
 * package (`@azure/msal-browser`) rather than the vendored script CIS
 * Capture uses, because this app is bundled by Vite. Same tenant, app
 * registration and API scope as CIS Capture (src/config.js AUTH_CONFIG) —
 * no SWA-managed auth, no linked backend.
 */

"use strict";

import { PublicClientApplication, InteractionRequiredAuthError, BrowserAuthError, BrowserAuthErrorCodes, LogLevel } from "@azure/msal-browser";
import { AUTH_CONFIG } from "../../config.js";

let pca = null;
let ready = null;

// Bounds acquireTokenSilent — a hung silent renewal iframe (root cause of
// the 29 Sep live defect: every screen stuck on "Loading…" with ZERO
// requests reaching the server because getToken() never settled) must not
// hang getToken() forever.
let SILENT_TOKEN_TIMEOUT_MS = 10000;

// Bounds init() (pca.initialize() + handleRedirectPromise()) for App's
// startup gate — a hung init must not leave that gate hanging either
// (review round 2, point 4).
let INIT_TIMEOUT_MS = 15000;

// Run 36977534443 review finding: auth_trace showed handle_redirect_start
// with no handle_redirect_end and no handle_redirect_error at all — msal's
// own handleRedirectPromise() never settled on that page load, despite the
// 3 repeated removeItem(request.params/interaction.status) pairs proving
// its internal cleanup branch DID run synchronously first. This watchdog
// never cancels or races the real call (that would risk acting on a stale
// outcome later) — it only RECORDS that the call was still pending after
// this long, so a live run can distinguish "hung forever" from "settled
// but just slow".
let REDIRECT_PENDING_WARNING_MS = 10000;

/** Test-only seam: shrinks the timeouts above so auth.test.js can prove timeout behaviour without a real multi-second wait. Never used outside tests. */
export function __setTimeoutsForTests({ silentTokenTimeoutMs, initTimeoutMs, redirectPendingWarningMs } = {}) {
  SILENT_TOKEN_TIMEOUT_MS = silentTokenTimeoutMs ?? 10000;
  INIT_TIMEOUT_MS = initTimeoutMs ?? 15000;
  REDIRECT_PENDING_WARNING_MS = redirectPendingWarningMs ?? 10000;
}

// msal-browser's own interaction-in-progress guard is a single sessionStorage
// key (temporaryCacheLocation defaults to sessionStorage; this app never
// overrides it — see msalConfig below), not namespaced per account/session.
// If a previous redirect sign-in never completed (tab closed mid-flow, a
// crashed handleRedirectPromise, …) this key is left set, and
// acquireTokenRedirect throws BrowserAuthError "interaction_in_progress"
// forever after. Clearing it before retrying is msal-browser's own
// documented workaround for a stale flag (no public API removes it — see
// node_modules/@azure/msal-browser BrowserCacheManager#setInteractionInProgress,
// which reads/writes exactly this key with no accompanying "reset" method).
const INTERACTION_STATUS_KEY = "msal.interaction.status";

// --- Single-flight + one-redirect-per-page-load guards (review round 2,
// point 2) ---------------------------------------------------------------
//
// Today's and Reports' loading effects call getToken() within the same
// tick. Before this guard, each concurrent call ran its own
// acquireTokenSilent, and if both timed out / hit InteractionRequired, both
// tried acquireTokenRedirect: the first sets msal's interaction.status
// flag, the second sees it, and (with the naive "always clear and retry"
// fix from round 1) cleared the FIRST call's still-live flag and fired a
// second redirect — a double navigation. Fixed two ways:
//   1. `tokenPromise` — every concurrent getToken() call in flight shares
//      the SAME underlying promise, so only one acquireTokenSilent (and
//      therefore only one possible redirect decision) ever runs per batch.
//   2. `redirectStarted` — a per-page-load, set-once flag. Even across
//      separate (non-concurrent) batches, at most one acquireTokenRedirect
//      is ever attempted. Because of this guard, the ONLY way
//      startInteractiveSignIn's own acquireTokenRedirect call can observe
//      msal's interaction_in_progress error is if the flag was already set
//      by something outside this page load (a crashed previous tab/session)
//      — never by a redirect this page load itself started — so clearing it
//      is always safe when it happens, never a race with our own call.
let tokenPromise = null;
let redirectStarted = false;
let initHandledRedirect = false;
// True only when THIS page load's handleRedirectPromise() returned a real (non-null) response that yielded an account.
let initProcessedRedirectAccount = false;
// True for exactly as long as THIS page load's init() IIFE (pca.initialize()
// + handleRedirectPromise()) is actually running, including one orphaned by
// initBounded()'s own timeout (review round 3 on PR #1866 — see init()'s own
// comment for why resetInit() must never discard it while this is true:
// serializing on ONE in-flight init(), never a second PCA, is what actually
// closes the race, not skipping handleRedirectPromise()).
let initInFlight = false;

// Counts every init() call that actually creates a NEW PCA (i.e. every
// init() invocation that was not served from a cached `ready` — see
// init()'s own comment for why that should be impossible given this
// module's singleton design). GitHub Actions run 36974281824: landing
// showed THREE repeated removeItem(request.params)/removeItem(
// interaction.status) pairs with no setItem for an account/token and no
// token-endpoint request — a pattern a SINGLE handleRedirectPromise() call
// can already produce on its own (msal's own
// BrowserCacheManager#cleanRequestByInteractionType loops over every
// stale Redirect-type request state and resets each one), so this counter
// exists to settle, from the next live run's trace, whether that page load
// actually ran more than one PCA/handleRedirectPromise rather than one call
// looping over stale state.
let pcaInstanceCount = 0;

// --- Sign-in phase trace (GitHub Actions run 36970238260 review finding):
// names/codes only, gated behind nothing (always on — harmless in
// production, and the whole point is to catch an intermittent live
// failure, not something reproducible on demand in a dev build). Exposed
// on `window.__smCockpitAuthTrace` so the CEO cockpit broker's
// `signInToCisCockpit` can read it via `page.evaluate()` and fold it into
// `diag.steps` (e.g. whether handle_redirect_start/`_end` ever ran on the
// landing page at all) — a module-level array is kept regardless of
// `window`'s existence (this module is also imported by a plain
// `node --test` run with no DOM) and mirrored onto `window` whenever it
// exists. Every init()/handle_redirect phase carries the PCA instance id
// that produced it (`#<n>`, GitHub Actions run 36974281824 review finding)
// so a trace with more than one distinct id proves multiple PCA instances
// ran on the same page, and a trace that never gets past id 1 proves they
// did not.
// Raised from 50 to 150 (GitHub Actions run 36977534443 review finding):
// msal's own verbose logger (see msalConfig's loggerOptions below) can push
// many entries per init() cycle, and the trace must still have room for the
// app's own phase markers around them.
const AUTH_TRACE_MAX_ENTRIES = 150;
let authTrace = [];

function recordAuthTrace(phase) {
  try {
    authTrace.push(phase);
    if (authTrace.length > AUTH_TRACE_MAX_ENTRIES) authTrace.shift();
    if (typeof window !== "undefined") {
      window.__smCockpitAuthTrace = authTrace;
    }
  } catch {
    // Diagnostics only — never let a recording failure break sign-in.
  }
}

/** Test-only seam: reads the phase trace without needing a `window` (plain `node --test`, no DOM). Never used outside tests. */
export function __getAuthTraceForTests() {
  return authTrace.slice();
}

// --- Response-location robustness (GitHub Actions run 36974281824 review
// finding) --------------------------------------------------------------
//
// msalConfig() never sets `auth.OIDCOptions.serverResponseType`, so it
// defaults to FRAGMENT (@azure/msal-browser Configuration.mjs), and
// msal-browser's RedirectClient#getRedirectResponse therefore ONLY ever
// reads `window.location.hash` when we call `handleRedirectPromise()` with
// no argument — it never looks at `window.location.search`. If Microsoft's
// redirect back to this app ever lands the authorization response in the
// query string instead of the fragment, `window.location.hash` is empty,
// handleRedirectPromise finds no response, and takes the "no server
// response" cleanup branch — discarding the real, valid code/state
// silently (see the pcaInstanceCount comment above for why that branch
// alone reproduces the exact repeated-removeItem pattern from run
// 36974281824, with no account/token ever set and no token-endpoint call).
//
// msal-common's UrlUtils#getDeserializedResponse strips a leading "#" OR
// "?" transparently before parsing, so passing the query string explicitly
// as the `hash` argument works identically to passing a real hash — this
// detects, at module load (before anything else can touch the URL),
// whichever of hash/search actually looks like an OAuth response (a
// `state=` plus a `code=` or `error=`) and passes THAT explicitly into
// handleRedirectPromise(), rather than always leaving msal to re-read
// `window.location.hash` live. Captured at module load (not inside init(),
// which only runs once React has mounted and effects have been scheduled)
// so it reflects the EARLIEST possible observation of the URL, immune to
// anything that could change it in between.
function looksLikeOauthResponse(value) {
  return typeof value === "string" && /state=/.test(value) && (/code=/.test(value) || /error=/.test(value));
}

let capturedRedirectResponse = "";
let capturedRedirectResponseLocation = "none";

function captureRedirectResponseLocation() {
  let hash = "";
  let search = "";
  try {
    hash = typeof window !== "undefined" ? String(window.location.hash || "") : "";
    search = typeof window !== "undefined" ? String(window.location.search || "") : "";
  } catch {
    hash = "";
    search = "";
  }
  if (looksLikeOauthResponse(hash)) {
    capturedRedirectResponse = hash;
    capturedRedirectResponseLocation = "hash";
  } else if (looksLikeOauthResponse(search)) {
    capturedRedirectResponse = search;
    capturedRedirectResponseLocation = "query";
  } else {
    capturedRedirectResponse = "";
    capturedRedirectResponseLocation = "none";
  }
  recordAuthTrace(`redirect_response_location:${capturedRedirectResponseLocation}`);
}

captureRedirectResponseLocation();

/** Test-only seam: simulates the app landing with the OAuth response in a given location, without needing a real DOM/window. Never used outside tests. */
export function __setCapturedRedirectResponseForTests(value, location) {
  capturedRedirectResponse = value || "";
  capturedRedirectResponseLocation = location || "none";
  recordAuthTrace(`redirect_response_location:${capturedRedirectResponseLocation}`);
}

/** Test-only seam: lets auth.test.js inject a fake PublicClientApplication instead of constructing the real MSAL one, and reset all module-level singleton state between tests. Never used outside tests. */
let pcaFactory = (config) => new PublicClientApplication(config);
export function __setPcaFactoryForTests(factory) {
  pcaFactory = factory || ((config) => new PublicClientApplication(config));
}
export function __resetForTests() {
  pca = null;
  ready = null;
  tokenPromise = null;
  redirectStarted = false;
  initHandledRedirect = false;
  initProcessedRedirectAccount = false;
  initInFlight = false;
  pcaInstanceCount = 0;
  authTrace = [];
  capturedRedirectResponse = "";
  capturedRedirectResponseLocation = "none";
  SILENT_TOKEN_TIMEOUT_MS = 10000;
  INIT_TIMEOUT_MS = 15000;
  REDIRECT_PENDING_WARNING_MS = 10000;
}

function clearStaleInteractionState() {
  try {
    if (typeof sessionStorage !== "undefined") sessionStorage.removeItem(INTERACTION_STATUS_KEY);
  } catch {
    // sessionStorage unavailable (private browsing, non-browser test env) — nothing to clear.
  }
}

/**
 * Runs `fn()` and resolves with `{ value }`, `{ error }` (fn threw or
 * rejected) or `{ timedOut: true }` (neither happened within `ms`) — never
 * rejects, regardless of what `fn` does. `fn` is a thunk so a SYNCHRONOUS
 * throw is caught the same as an asynchronous rejection. Same shape as
 * api/client.js#withTimeout (kept independent — this module has no
 * dependency on client.js).
 */
function withTimeoutOutcome(fn, ms) {
  return new Promise((resolve) => {
    let done = false;
    const timer = setTimeout(() => {
      if (!done) {
        done = true;
        resolve({ timedOut: true });
      }
    }, ms);
    Promise.resolve()
      .then(fn)
      .then(
        (value) => {
          if (!done) {
            done = true;
            clearTimeout(timer);
            resolve({ value });
          }
        },
        (error) => {
          if (!done) {
            done = true;
            clearTimeout(timer);
            resolve({ error });
          }
        }
      );
  });
}

/**
 * Starts a fresh redirect sign-in for `account` — the ONLY path in this
 * module that ever navigates the browser. Guarded to at most once per page
 * load (see `redirectStarted` above). Returns `true` if a redirect was
 * actually started (the browser is navigating away, or about to), `false`
 * if this page load had already started one (nothing more to do) or the
 * attempt itself failed. Never throws.
 */
async function startInteractiveSignIn(account) {
  if (redirectStarted) return false;
  redirectStarted = true;
  try {
    await pca.acquireTokenRedirect({ scopes: [AUTH_CONFIG.apiScope], account });
    return true;
  } catch (err) {
    if (err instanceof BrowserAuthError && err.errorCode === BrowserAuthErrorCodes.interactionInProgress && initHandledRedirect) {
      // Reachable only via a flag left by something OUTSIDE this page load:
      // `redirectStarted` was false when this function was entered (the
      // guard above), so this is this page load's first and only
      // acquireTokenRedirect attempt — it cannot be colliding with another
      // call this same load made. Safe to clear and retry exactly once.
      clearStaleInteractionState();
      try {
        await pca.acquireTokenRedirect({ scopes: [AUTH_CONFIG.apiScope], account });
        return true;
      } catch {
        redirectStarted = false; // nothing is navigating; a user click must be able to retry.
        return false;
      }
    }
    redirectStarted = false;
    return false;
  }
}

// --- MSAL's own verbose logger, surfaced into the trace (GitHub Actions
// run 36977534443 review finding: "We can't see MSAL's decisions.") -------
//
// msal-browser's own internal branching (which response-handling path it
// took, why it decided there was no server response, …) is otherwise
// invisible to this app. `system.loggerOptions` is msal's own supported
// extension point for this — `piiLoggingEnabled: false` keeps msal itself
// from ever handing the callback anything it considers PII, and the
// callback below only ever pushes a sanitized string into the in-memory
// trace array; it never touches storage, the network, or anything visible
// outside this page's own memory, so this is harmless in production both
// by msal's own PII gate and by what the callback does with what it gets.
const GUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const JWT_LIKE_RE = /\b[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g;
const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const MSAL_LOG_MESSAGE_MAX_LENGTH = 160;

function sanitizeMsalLogMessage(message) {
  let out = String(message || "");
  // Cut at the first code=/state=/# — never record anything that follows
  // one of these, however it got into the message (msal's verbose logs
  // sometimes echo a URL or request params).
  const cutMatch = out.match(/code=|state=|#/);
  if (cutMatch) out = `${out.slice(0, cutMatch.index)}<redacted>`;
  out = out.replace(GUID_RE, "<id>").replace(JWT_LIKE_RE, "<jwt>").replace(EMAIL_RE, "<email>");
  if (out.length > MSAL_LOG_MESSAGE_MAX_LENGTH) out = out.slice(0, MSAL_LOG_MESSAGE_MAX_LENGTH);
  return out;
}

/** Test-only seam: exercises the sanitizer directly. Never used outside tests. */
export function __sanitizeMsalLogMessageForTests(message) {
  return sanitizeMsalLogMessage(message);
}

function msalConfig() {
  return {
    auth: {
      clientId: AUTH_CONFIG.clientId,
      authority: `https://login.microsoftonline.com/${AUTH_CONFIG.tenantId}`,
      redirectUri: typeof window !== "undefined" ? window.location.origin : "https://example.invalid",
    },
    cache: { cacheLocation: "localStorage", storeAuthStateInCookie: true },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Verbose,
        piiLoggingEnabled: false,
        loggerCallback: (_level, message) => {
          recordAuthTrace(`msal:${sanitizeMsalLogMessage(message)}`);
        }
      }
    },
  };
}

/**
 * Initialize MSAL and process any redirect response. Idempotent —
 * concurrent/later callers all share the same underlying promise, and
 * NEVER skips `handleRedirectPromise()` — on any page, regardless of
 * whether a redirect has started (round-3 review finding on PR #1866: an
 * earlier version of this fix skipped the call once `redirectStarted` was
 * true, which also skipped it on the RETURN page itself if the user
 * clicked "Sign in"/"Sign in again" again while this call was still
 * processing the real response — reopening the same race on the return
 * leg instead of closing it).
 *
 * `initInFlight` is true for exactly as long as this IIFE is running
 * (`finally` flips it back regardless of success/failure) — `resetInit()`
 * checks it so that AT MOST ONE `init()` is ever in flight per page load:
 * GitHub Actions run 36966071389 root cause was `initBounded()`'s own
 * `INIT_TIMEOUT_MS` bound not cancelling this IIFE, so a slow
 * `pca.initialize()` kept running in the background after the caller
 * stopped waiting; a user-initiated "Retry" (`resetInit()`) used to
 * discard `ready`/`pca` and start a SECOND one regardless, so when the
 * orphaned first call's `pca.initialize()` finally resolved, its
 * `handleRedirectPromise()` ran — on the SAME page, before any response
 * hash existed — and msal-browser's own "no server response" branch
 * unconditionally cleared `msal.interaction.status`; the real response
 * then returned from Microsoft to find `isInteractionInProgress()` false
 * and was silently dropped. Serializing on one `ready` promise (never a
 * second PCA) removes the precondition for that race entirely, rather than
 * trying to skip the one call that happens to be dangerous.
 */
export function init() {
  if (ready) return ready;
  initInFlight = true;
  pcaInstanceCount += 1;
  const instanceId = pcaInstanceCount;
  recordAuthTrace(`init_start#${instanceId}`);
  ready = (async () => {
    try {
      pca = pcaFactory(msalConfig());
      await pca.initialize();
      let account = null;
      recordAuthTrace(`handle_redirect_start#${instanceId}`);
      // Run 36977534443 review finding: records whether the call is STILL
      // pending once this long has passed, without cancelling or racing it
      // — the real `await` below keeps waiting for whatever msal eventually
      // does, however long that takes.
      let handleRedirectSettled = false;
      const pendingWarningTimer = setTimeout(() => {
        if (!handleRedirectSettled) recordAuthTrace(`handle_redirect_still_pending_after_10s#${instanceId}`);
      }, REDIRECT_PENDING_WARNING_MS);
      try {
        const resp = await pca.handleRedirectPromise(capturedRedirectResponse || undefined);
        handleRedirectSettled = true;
        clearTimeout(pendingWarningTimer);
        if (resp && resp.account) initProcessedRedirectAccount = true;
        account = (resp && resp.account) || pca.getAllAccounts()[0] || null;
      } catch (err) {
        handleRedirectSettled = true;
        clearTimeout(pendingWarningTimer);
        recordAuthTrace(`handle_redirect_error:${(err && err.errorCode) || "unknown"}#${instanceId}`);
        account = pca.getAllAccounts()[0] || null;
      } finally {
        initHandledRedirect = true;
      }
      recordAuthTrace(`${account ? "handle_redirect_end(account)" : "handle_redirect_end(no_account)"}#${instanceId}`);
      if (account) pca.setActiveAccount(account);
      return { account };
    } finally {
      initInFlight = false;
      recordAuthTrace(`init_end#${instanceId}`);
    }
  })();
  return ready;
}

/**
 * Bounded wrapper around init() for App's startup gate (review round 2,
 * point 4) — resolves within `INIT_TIMEOUT_MS` no matter what init() does,
 * so the initial sign-in gate can never hang. Does not touch the cached
 * `ready` promise: a slow init() keeps running in the background (see
 * `initInFlight`) and a later init()/getToken()/login() call still
 * observes its real outcome once (or if) it settles — this wrapper only
 * bounds how long THIS caller waits.
 */
export async function initBounded() {
  const outcome = await withTimeoutOutcome(() => init(), INIT_TIMEOUT_MS);
  if (outcome.timedOut) return { ok: false, errorCode: "timeout" };
  if (outcome.error) return { ok: false, errorCode: "sign_in_required" };
  return { ok: true };
}

/**
 * Discards the cached init() so a user-initiated "Retry" gets a genuinely
 * fresh attempt after initBounded() failed or timed out — EXCEPT while
 * `initInFlight` (an init() for THIS page load is still actually running),
 * in which case this is a no-op: the in-flight one is reused/awaited by
 * whatever calls `init()` next, rather than orphaning it and starting a
 * second PCA (GitHub Actions run 36966071389 — see init()'s own comment).
 * A hung `pca.initialize()` therefore never gets a genuinely fresh retry
 * (nothing can cancel it), but msal-browser's shared, clientId-keyed
 * interaction-status key makes running two PCA instances concurrently
 * unsafe regardless of how long the first is taking.
 *
 * `reason` (a short label, e.g. "retry") is recorded into the phase trace
 * (GitHub Actions run 36974281824 review finding) so a live run shows
 * exactly why/when this ever ran — distinguishing a user-initiated Retry
 * from any other future caller — and whether it actually reset anything
 * or no-opped because init() was still in flight.
 */
export function resetInit(reason) {
  const label = reason || "unspecified";
  if (initInFlight) {
    recordAuthTrace(`reset_init:${label}:noop_in_flight`);
    return;
  }
  recordAuthTrace(`reset_init:${label}`);
  ready = null;
  pca = null;
}

export function getAccount() {
  return pca ? pca.getActiveAccount() || pca.getAllAccounts()[0] || null : null;
}

export function isSignedIn() {
  return Boolean(getAccount());
}

export function getUpn() {
  const a = getAccount();
  return a ? a.username || (a.idTokenClaims && a.idTokenClaims.preferred_username) || null : null;
}

export function getDisplayName() {
  const a = getAccount();
  return a ? a.name || getUpn() : null;
}

/**
 * Starts the user-initiated (explicit "Sign in" / "Sign in again" click)
 * redirect.
 *
 * `await init()` FIRST, unconditionally — this waits for ANY in-flight
 * init()/handleRedirectPromise() work (this page load's own, including one
 * orphaned by initBounded()'s timeout) to actually finish before anything
 * else happens. Because `resetInit()` never lets a second `init()` start
 * while one is already in flight (see its own comment), there is at most
 * ONE `handleRedirectPromise()` call per page load, and it always
 * completes before `loginRedirect()` ever runs — removing the
 * precondition for the GitHub Actions run 36966071389 race rather than
 * skipping the call on one side of it (the round-3 review finding: the
 * previous "skip if redirectStarted" version of this fix reopened the same
 * race on the RETURN page, if the user clicked Sign in again while THAT
 * page's own init() was still processing the real response).
 *
 * If `init()` just finished processing a REAL redirect response — the user
 * clicked while that was still in flight — `account` is now set; this ends
 * in the signed-in state and does NOT start a redundant second redirect.
 *
 * `redirectStarted` (the existing one-redirect-per-page-load guard shared
 * with `startInteractiveSignIn`) is set only right before `loginRedirect()`
 * itself, and cleared in a `catch` on ANY failure (`init()` throwing OR
 * `loginRedirect()` throwing) — round-3 review finding: a previous version
 * set it unconditionally up front with no catch, so a failure left it
 * stuck `true` forever, permanently blocking both Retry (`resetInit()`,
 * before this redesign) and any later Sign-in attempt
 * (`startInteractiveSignIn`'s own guard).
 */
export async function login() {
  recordAuthTrace("login_start");
  let result;
  try {
    result = await init();
  } catch (err) {
    // The cached init() rejected (settled, so none is in flight). Reset it
    // and try once more; resetInit() is a no-op while an init is in flight.
    recordAuthTrace("login_init_failed_retrying");
    resetInit("login");
    if (initInFlight) throw err;
    result = await init();
  }
  if (initProcessedRedirectAccount && result && result.account) {
    // Honour the flag for the FIRST login() after processing only; any
    // later click (or any token failure, see doGetToken) must redirect.
    initProcessedRedirectAccount = false;
    // THIS page load just processed a real redirect response that yielded
    // an account — end signed-in, never start a second redirect. (A merely
    // CACHED account does not count: that is the stuck "Sign in again"
    // defect, where a stale cached account swallowed the user's click.)
    return result;
  }
  if (redirectStarted) {
    // A redirect (user click or automatic) is already navigating this page
    // load - a second would double-navigate. Failed attempts release the
    // flag, so a failed automatic path cannot swallow a click.
    recordAuthTrace("login_blocked_redirect_already_started");
    return null;
  }
  redirectStarted = true; // stop automatic paths starting a second navigation.
  const account = (result && result.account) || getAccount();
  const start = () =>
    account
      ? pca.acquireTokenRedirect({ scopes: [AUTH_CONFIG.apiScope], account })
      : pca.loginRedirect({ scopes: [AUTH_CONFIG.apiScope] });
  try {
    recordAuthTrace(account ? "login_redirect_called(account)" : "login_redirect_called");
    return await start();
  } catch (err) {
    if (err instanceof BrowserAuthError && err.errorCode === BrowserAuthErrorCodes.interactionInProgress) {
      recordAuthTrace("login_retry_after_interaction_in_progress");
      clearStaleInteractionState();
      try {
        return await start();
      } catch (err2) {
        redirectStarted = false;
        throw err2;
      }
    }
    redirectStarted = false;
    throw err;
  }
}

export async function logout() {
  await init();
  return pca.logoutRedirect();
}

/**
 * The actual getToken logic — see getToken() below for the single-flight
 * wrapper every caller goes through. Silent-first, bounded to
 * `SILENT_TOKEN_TIMEOUT_MS`. Per review round 2 point 3: a redirect is
 * started ONLY for a genuine `InteractionRequiredAuthError` (msal's signal
 * that the account truly needs interaction — no usable refresh token, MFA
 * required, etc.) — never for a silent-path TIMEOUT or any other silent
 * failure, so a slow network never navigates the user away unprompted.
 * Resolves `null` only for the legitimate "not signed in" state (no
 * account) or once a redirect has actually been kicked off (the browser is
 * navigating away). Every OTHER failure — init failing, a silent timeout, a
 * silent error that isn't InteractionRequired, a redirect attempt that
 * couldn't run because this page load already started one — REJECTS, so
 * api/client.js's existing catch-all (any getToken rejection -> errorCode
 * "sign_in_required") surfaces an actionable "Sign in again", never a
 * navigation and never a hang.
 */
async function doGetToken() {
  try {
    await init();
  } catch {
    throw new Error("sign_in_required: init failed");
  }
  const account = getAccount();
  if (!account) return null; // genuinely not signed in — expected, not an error.

  const outcome = await withTimeoutOutcome(
    () => pca.acquireTokenSilent({ scopes: [AUTH_CONFIG.apiScope], account }),
    SILENT_TOKEN_TIMEOUT_MS
  );

  if (outcome.value) return outcome.value.accessToken;

  // A token failure on this page load: a later "Sign in again" click must redirect.
  initProcessedRedirectAccount = false;

  if (outcome.timedOut) {
    // Hung silent renewal (e.g. a stuck iframe) — NOT redirect-worthy.
    throw new Error("sign_in_required: silent token acquisition timed out");
  }

  if (outcome.error instanceof InteractionRequiredAuthError) {
    const started = await startInteractiveSignIn(account);
    if (!started) throw new Error("sign_in_required: redirect already started this page load");
    return null; // the browser is navigating away.
  }

  // Any other silent-path failure (offline, generic error, …): same
  // treatment as a timeout — no redirect, an actionable error instead.
  throw new Error("sign_in_required: silent token acquisition failed");
}

/**
 * Acquire an access token for the CIS API. Single-flight: concurrent
 * callers (Today's and Reports' loading effects fire in the same tick)
 * share one in-flight promise, so only one acquireTokenSilent call — and
 * therefore only one possible redirect decision — is ever made per batch
 * (review round 2, point 2). See doGetToken() above for the redirect-vs-
 * reject policy (point 3). May reject — callers must NOT assume this
 * never throws; api/client.js's getJson/postJson already treat any
 * rejection here as errorCode "sign_in_required" (the fix for the 29 Sep
 * "unhandled rejection -> stuck on Loading" defect is that boundary, not a
 * promise from this function never to reject).
 */
export function getToken() {
  if (!tokenPromise) {
    tokenPromise = doGetToken().finally(() => {
      tokenPromise = null;
    });
  }
  return tokenPromise;
}

/** Two letters for the avatar chip, from a display name or UPN. */
export function initialsFrom(nameOrUpn) {
  const value = String(nameOrUpn || "").trim();
  if (!value) return "?";
  const namePart = value.includes("@") ? value.split("@")[0] : value;
  const words = namePart.split(/[.\s_-]+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}
